import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import GenericMetricChart, {
  ChartDataItem,
} from '../../components/charts/GenericMetricChart';
import { Stat } from '../../components/stats/Stat';
import StatItem from '../../components/stats/StatItem';
import { getProcessTypeColor } from '../../dashboard/kpi/utilityFunctions';
import useActivityEventProcessTypeDetails from '../../import/activityFeed/schema/useActivityEventProcessTypeDetails';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useGetActualityAggregationGroupColors from '../hooks/useGetActualityAggregationGroupColors';
import useGetActualityAggregationGroupTitle from '../hooks/useGetActualityAggregationGroupTitle';
import { usePieChartMetrics } from '../hooks/usePieChartMetrics';
import { actualityAggregatedStatsByGroups } from '../store/actuality.kpi.state';
import { ActualityAggregationField } from '../store/actuality.kpi.types';
import ActualityAggregateMetricSelector from './ActualityAggregateMetricSelector';

interface FeedProcessPieProps {
  aggregateBy?: ActualityAggregationField;
  title?: string;
  titleElement?: React.ReactNode;
}

const FeedProcessPie: React.FC<FeedProcessPieProps> = ({
  aggregateBy = 'eventProcessType',
  title,
  titleElement,
}) => {
  const { t } = useTranslation('feed');
  const processTypeDetails = useActivityEventProcessTypeDetails();
  const aggFieldsDescriptors = useActualityAggregationFieldDescriptors();
  const getGroupTitle = useGetActualityAggregationGroupTitle();
  const getGroupColors = useGetActualityAggregationGroupColors();

  // Get aggregated stats based on the provided field
  const aggregatedStats = useRecoilValue(
    actualityAggregatedStatsByGroups([aggregateBy]),
  );

  const {
    currentMetric,
    setCurrentMetric,
    selectedAdvancedMetric,
    setSelectedAdvancedMetric,
    getMetricValue,
    getTotalValue,
    getTotalLabel,
    getFormattedValue,
    getMetricOptions,
  } = usePieChartMetrics(aggregatedStats);

  const data: ChartDataItem[] = _.chain(aggregatedStats)
    .map(row => {
      const key = String(row[aggregateBy]);

      // For eventProcessType, use the process type details for the title
      // For other fields, use the aggregation group title helper
      let label: string;
      if (aggregateBy === 'eventProcessType') {
        label =
          processTypeDetails.find(detail => detail.id === row.eventProcessType)
            ?.title ||
          row.eventProcessType ||
          'Unknown Process';
      } else {
        label = getGroupTitle(row, [aggregateBy]);
      }

      const value = getMetricValue(row);

      // Get color based on aggregation type
      let color: [string, string];
      if (aggregateBy === 'eventProcessType') {
        color = getProcessTypeColor(key);
      } else {
        const colors = getGroupColors(row, [aggregateBy], ['group']);
        color = [colors[0], colors[1]];
      }

      return {
        id: key,
        value: value,
        label: label,
        color: color,
        formatted: getFormattedValue(value),
      };
    })
    .filter(item => item.value > 0)
    .sortBy(item => -item.value)
    .value();

  const totalValue = getTotalValue();
  const formattedTotal = getFormattedValue(totalValue);

  const kpiSwitch = (
    <div data-component="feed-process-pie-kpi-switch" className="my-4">
      <ButtonSwitchMulti
        autoSize
        buttonType="minimal"
        options={getMetricOptions()}
        selectedIndex={[
          'share',
          'events',
          'duration',
          'cost',
          'distance',
          'advanced',
        ].indexOf(currentMetric)}
        onClick={index => {
          setCurrentMetric(
            ['share', 'events', 'duration', 'cost', 'distance', 'advanced'][
              index
            ] as any,
          );
        }}
      />

      {currentMetric === 'advanced' && (
        <div
          data-component="feed-process-pie-advanced-metric-selector"
          className="my-2 w-full"
        >
          <ActualityAggregateMetricSelector
            value={selectedAdvancedMetric}
            onChange={setSelectedAdvancedMetric}
          />
        </div>
      )}
    </div>
  );

  if (data.length === 0) {
    return null;
  }

  // Determine the title based on the aggregation field or use provided title
  const panelTitle =
    title || aggFieldsDescriptors[aggregateBy]?.title || t`Summary`;

  return (
    <Stat
      key={`panel-feed-global-filters-${aggregateBy}`}
      title={titleElement ? null : panelTitle}
      titleElement={titleElement}
      className="p-4"
    >
      {kpiSwitch}
      <GenericMetricChart
        data={data}
        // metricDescriptor={aggFieldsDescriptors[aggregateBy]}
        showChartTypeSelector={false}
        containerHeight={300}
        showCenterPie
      />
      {/* <ContainerChart containerMaxHeight={200}>
        <StatCenterPie
          statLabelMiddle={getTotalLabel()}
          statValueMiddle={formattedTotal.value}
        />
        <SimulationPieChart data={data} />
      </ContainerChart> */}
      {_(data)
        .map(item => (
          <StatItem
            key={`legend-item-${item.id}`}
            title={item.label}
            // value={item.formatted} -- crashes
            // value={item.formatted.value} // -- crashes when 3 dots
            value={item.formatted}
            legendColor={item.color[0]}
          />
        ))
        .value()}
    </Stat>
  );
};

export default FeedProcessPie;
